import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import VersionDisplay from './VersionDisplay';

const Navigation: React.FC = () => {
  const { isAuthenticated, user, userRole, logout, redirectToLogin, redirectToSignup } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
      <div className="container">
        <Link className="navbar-brand d-flex align-items-center" to="/">
          <img
            src="/ratemytalk150.png"
            alt="RateMyTalk Logo"
            height="40"
            className="me-2"
          />
          RateMyTalk
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          onClick={toggleMenu}
          aria-controls="navbarNav"
          aria-expanded={isMenuOpen}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`} id="navbarNav">
          {isAuthenticated ? (
            <AuthenticatedNav
              user={user}
              userRole={userRole}
              onLogout={handleLogout}
              isActivePath={isActivePath}
            />
          ) : (
            <UnauthenticatedNav
              onLogin={redirectToLogin}
              onSignup={redirectToSignup}
              isActivePath={isActivePath}
            />
          )}
        </div>

        <div className="d-none d-lg-block ms-3">
          <VersionDisplay />
        </div>
      </div>
    </nav>
  );
};

interface AuthenticatedNavProps {
  user: any;
  userRole: string | null;
  onLogout: () => void;
  isActivePath: (path: string) => boolean;
}

const AuthenticatedNav: React.FC<AuthenticatedNavProps> = ({
  user,
  userRole,
  onLogout,
  isActivePath
}) => {
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState(0);
  const userDropdownRef = useRef<HTMLLIElement>(null);
  const adminDropdownRef = useRef<HTMLLIElement>(null);

  const toggleUserDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUserDropdownOpen(!isUserDropdownOpen);
    setIsAdminDropdownOpen(false); // Close admin dropdown
  };

  const toggleAdminDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdminDropdownOpen(!isAdminDropdownOpen);
    setIsUserDropdownOpen(false); // Close user dropdown
  };

  const closeDropdowns = () => {
    setIsUserDropdownOpen(false);
    setIsAdminDropdownOpen(false);
  };

  // Load pending invitations count
  useEffect(() => {
    const loadInvitations = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://api.devtalks.headforthe.cloud'}/v1/invitations`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('id_token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          const invitations = data.data || [];
          const pending = invitations.filter((inv: any) => inv.status === 'pending');
          setPendingInvitations(pending.length);
        }
      } catch (err) {
        console.error('Failed to load invitations:', err);
      }
    };

    loadInvitations();
    // Refresh every 60 seconds
    const interval = setInterval(loadInvitations, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node) &&
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <ul className="navbar-nav me-auto">
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/dashboard') ? 'active' : ''}`}
            to="/dashboard"
            onClick={closeDropdowns}
          >
            <i className="fas fa-home me-1"></i>
            Dashboard
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/talks') ? 'active' : ''}`}
            to="/talks"
            onClick={closeDropdowns}
          >
            <i className="fas fa-microphone me-1"></i>
            My Talks
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/sessions') ? 'active' : ''}`}
            to="/sessions"
            onClick={closeDropdowns}
          >
            <i className="fas fa-calendar-alt me-1"></i>
            My Sessions
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/events') ? 'active' : ''}`}
            to="/events"
            onClick={closeDropdowns}
          >
            <i className="fas fa-calendar me-1"></i>
            Events
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/invitations') ? 'active' : ''}`}
            to="/invitations"
            onClick={closeDropdowns}
          >
            <i className="fas fa-envelope me-1"></i>
            Invitations
            {pendingInvitations > 0 && (
              <span className="badge bg-warning ms-1">{pendingInvitations}</span>
            )}
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/messages') ? 'active' : ''}`}
            to="/messages"
            onClick={closeDropdowns}
          >
            <i className="fas fa-comment me-1"></i>
            Messages
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/speakers') ? 'active' : ''}`}
            to="/speakers"
            onClick={closeDropdowns}
          >
            <i className="fas fa-users me-1"></i>
            Find Speakers
          </Link>
        </li>
        {userRole === 'admin' && (
          <li className="nav-item dropdown" ref={adminDropdownRef}>
            <button
              className="nav-link dropdown-toggle btn btn-link"
              onClick={toggleAdminDropdown}
              aria-expanded={isAdminDropdownOpen}
            >
              <i className="fas fa-cog me-1"></i>
              Admin
            </button>
            <ul className={`dropdown-menu ${isAdminDropdownOpen ? 'show' : ''}`}>
              <li>
                <Link className="dropdown-item" to="/admin/users" onClick={closeDropdowns}>
                  <i className="fas fa-users me-2"></i>
                  User Management
                </Link>
              </li>
              <li>
                <Link className="dropdown-item" to="/admin/roles" onClick={closeDropdowns}>
                  <i className="fas fa-crown me-2"></i>
                  Admin Roles
                </Link>
              </li>
              <li>
                <Link className="dropdown-item" to="/admin/invitations" onClick={closeDropdowns}>
                  <i className="fas fa-user-plus me-2"></i>
                  Admin Invitations
                </Link>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <Link className="dropdown-item" to="/admin/settings" onClick={closeDropdowns}>
                  <i className="fas fa-cog me-2"></i>
                  System Settings
                </Link>
              </li>
              <li>
                <Link className="dropdown-item" to="/admin/audit-log" onClick={closeDropdowns}>
                  <i className="fas fa-history me-2"></i>
                  Audit Log
                </Link>
              </li>
            </ul>
          </li>
        )}
      </ul>

      <ul className="navbar-nav">
        <li className="nav-item dropdown" ref={userDropdownRef}>
          <button
            className="nav-link dropdown-toggle btn btn-link d-flex align-items-center"
            onClick={toggleUserDropdown}
            aria-expanded={isUserDropdownOpen}
          >
            {user?.profile_image_url ? (
              <img
                src={user.profile_image_url}
                alt="Profile"
                className="rounded-circle me-2"
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  border: '2px solid #fff'
                }}
              />
            ) : (
              <i className="fas fa-user me-2"></i>
            )}
            {user?.name || user?.email}
            {userRole === 'admin' && (
              <span className="badge bg-danger ms-2">ADMIN</span>
            )}
          </button>
          <ul className={`dropdown-menu dropdown-menu-end ${isUserDropdownOpen ? 'show' : ''}`}>
            <li>
              <Link className="dropdown-item" to="/profile" onClick={closeDropdowns}>
                <i className="fas fa-user-edit me-2"></i>
                User Profile
              </Link>
            </li>
            <li>
              <Link className="dropdown-item" to="/settings" onClick={closeDropdowns}>
                <i className="fas fa-cog me-2"></i>
                Settings
              </Link>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button className="dropdown-item" onClick={() => { closeDropdowns(); onLogout(); }}>
                <i className="fas fa-sign-out-alt me-2"></i>
                Logout
              </button>
            </li>
          </ul>
        </li>
      </ul>
    </>
  );
};

interface UnauthenticatedNavProps {
  onLogin: () => void;
  onSignup: () => void;
  isActivePath: (path: string) => boolean;
}

const UnauthenticatedNav: React.FC<UnauthenticatedNavProps> = ({
  onLogin,
  onSignup,
  isActivePath
}) => {
  return (
    <>
      <ul className="navbar-nav me-auto">
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/') ? 'active' : ''}`}
            to="/"
          >
            <i className="fas fa-home me-1"></i>
            Home
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${isActivePath('/events/public') ? 'active' : ''}`}
            to="/events/public"
          >
            <i className="fas fa-calendar me-1"></i>
            Public Events
          </Link>
        </li>
      </ul>

      <ul className="navbar-nav">
        <li className="nav-item">
          <button
            className="nav-link btn btn-link"
            onClick={onLogin}
          >
            <i className="fas fa-sign-in-alt me-1"></i>
            Login
          </button>
        </li>
        <li className="nav-item">
          <button
            className="nav-link btn btn-link"
            onClick={onSignup}
          >
            <i className="fas fa-user-plus me-1"></i>
            Sign Up
          </button>
        </li>
      </ul>
    </>
  );
};

export default Navigation;
